import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, AlertTriangle, Award, Users, BookOpen, CalendarCheck, Flame, RefreshCcw, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function StatTile({ icon: Icon, label, value, hint, tone = "forest" }) {
  const tones = {
    forest: "text-forest-700",
    gold: "text-gold-600",
    terracotta: "text-terracotta-600",
  };
  return (
    <div className="rounded-2xl border border-sand-300 bg-white/70 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${tones[tone]}`} />
        {label}
      </div>
      <div className={`mt-1.5 text-2xl font-display font-semibold ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function StudentRow({ s, danger, onNudge, nudging }) {
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`pulse-student-${s.user_id}`}>
      {s.picture ? (
        <img src={s.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-forest-700 text-sand-100 grid place-items-center text-xs font-display font-semibold">
          {(s.name || "?")[0].toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{s.name || "Unnamed"}</div>
        <div className="text-[11px] text-muted-foreground">
          {s.active_days}d active · {s.recitation_count} recitations · {s.ayahs_memorized_week} ayahs
        </div>
      </div>
      <div className={`text-sm font-display font-semibold ${danger ? "text-terracotta-600" : "text-forest-700"}`}>
        {fmtPct(s.avg_accuracy)}
      </div>
      {onNudge && (
        <button
          onClick={() => onNudge(s)}
          disabled={nudging}
          data-testid={`nudge-${s.user_id}`}
          className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10 disabled:opacity-50"
          aria-label={`Nudge ${s.name}`}
        >
          {nudging ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          Nudge
        </button>
      )}
    </li>
  );
}

function ClassCard({ c, onNudge, nudgingId }) {
  return (
    <article className="hifz-card space-y-5" data-testid={`halaqa-card-${c.class_id}`}>
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-xl text-forest-700">{c.class_name}</h2>
          {c.schedule && <p className="text-xs text-muted-foreground mt-0.5">{c.schedule}</p>}
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-forest-600/8 text-forest-700 text-xs font-medium">
            <Users className="h-3 w-3" />
            {c.active_students}/{c.student_count} active this week
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3">
        <StatTile icon={Activity} label="Cohort accuracy" value={fmtPct(c.avg_accuracy)} hint="Avg of student weekly avgs" />
        <StatTile icon={BookOpen} label="Ayahs this week" value={c.total_ayahs_week} tone="gold" />
        <StatTile icon={CalendarCheck} label="Avg attendance" value={c.avg_attendance_count} hint="Sessions / student" />
        <StatTile icon={Flame} label="At-risk" value={c.at_risk.length} tone="terracotta" hint="Students needing attention" />
      </div>

      {c.student_count === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No students enrolled yet. Share the join link to start your cohort.
        </div>
      ) : (
        <div className="grid gap-5">
          {/* Top mistakes */}
          <div className="rounded-2xl border border-sand-300 bg-sand-200/40 p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
              <RefreshCcw className="h-3 w-3" /> Cohort top mistakes
            </div>
            {c.top_mistakes.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No mistake patterns this week — mashallah.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid={`cohort-mistakes-${c.class_id}`}>
                {c.top_mistakes.map((m, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="ayah-arabic text-lg text-forest-700" dir="rtl">{m.word}</span>
                    <span className="text-xs text-muted-foreground">{m.count}×</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* At-risk */}
          <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-terracotta-600 font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" /> Needs your attention
            </div>
            {c.at_risk.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">Every student is engaged. Tabarakallah.</p>
            ) : (
              <ul className="mt-2 divide-y divide-sand-300/60" data-testid={`at-risk-${c.class_id}`}>
                {c.at_risk.map((s) => <StudentRow key={s.user_id} s={s} danger onNudge={onNudge} nudging={nudgingId === s.user_id} />)}
              </ul>
            )}
          </div>

          {/* Top performers */}
          <div className="rounded-2xl border border-gold-500/40 bg-gold-500/5 p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gold-600 font-semibold flex items-center gap-1.5">
              <Award className="h-3 w-3" /> Top performers
            </div>
            {c.top_performers.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No active students this week yet.</p>
            ) : (
              <ul className="mt-2 divide-y divide-sand-300/60" data-testid={`top-performers-${c.class_id}`}>
                {c.top_performers.map((s) => <StudentRow key={s.user_id} s={s} />)}
              </ul>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function HalaqaPulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgingId, setNudgingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/teacher/halaqa-pulse");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load halaqa pulse");
    } finally {
      setLoading(false);
    }
  };

  const handleNudge = async (student) => {
    setNudgingId(student.user_id);
    try {
      await api.post("/teacher/nudge", { student_id: student.user_id });
      toast.success(`Nudge sent to ${(student.name || "student").split(" ")[0]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send nudge");
    } finally {
      setNudgingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/teacher/halaqa-pulse");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't load halaqa pulse");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading && !data) {
    return <div className="text-muted-foreground" data-testid="halaqa-pulse-loading">Aggregating your cohort…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="halaqa-pulse-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Halaqa Pulse</div>
          <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Your cohort, at a glance</h1>
          <p className="mt-2 text-foreground/70 max-w-2xl">
            Weekly snapshot across every halaqa you teach — average accuracy, attendance, top cohort mistakes, and the students who need your attention most.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-2xl border border-sand-300 bg-white/70 px-4 py-2 text-sm">
            <span className="text-muted-foreground">Total students</span>
            <span className="ml-2 font-display font-semibold text-forest-700" data-testid="total-students">{data.totals?.student_count ?? 0}</span>
          </div>
          <div className="rounded-2xl border border-sand-300 bg-white/70 px-4 py-2 text-sm">
            <span className="text-muted-foreground">Active this week</span>
            <span className="ml-2 font-display font-semibold text-gold-600" data-testid="total-active">{data.totals?.active_students ?? 0}</span>
          </div>
          <button onClick={load} className="btn-accent text-sm" data-testid="halaqa-pulse-refresh">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </header>

      {data.classes.length === 0 ? (
        <div className="hifz-card text-center py-16">
          <div className="mx-auto h-12 w-12 rounded-full bg-sand-200 grid place-items-center text-forest-700">
            <Users className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-xl text-forest-700">No halaqas yet</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Create a halaqa from the Halaqa page to start aggregating your students&apos; weekly pulse here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.classes.map((c) => <ClassCard key={c.class_id} c={c} onNudge={handleNudge} nudgingId={nudgingId} />)}
        </div>
      )}
    </div>
  );
}
